import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import OperisLoader from './components/common/OperisLoader';
import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import { SignalRProvider } from './context/SignalRContext';
import { TimerProvider } from './context/TimerContext';
import { TabPermissionProvider } from './context/TabPermissionContext';
import TabGuard from './components/common/TabGuard';


// Layouts
import MainLayout from './components/layout/MainLayout';
import GlobalBugButton from './components/common/GlobalBugButton';

// Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import TermsPage from './pages/auth/TermsPage';
import PrivacyPolicyPage from './pages/auth/PrivacyPolicyPage';
import LandingPage from './pages/auth/LandingPage';
import Dashboard from './pages/dashboard/DashboardNew';
import ProfilePage from './pages/profile/ProfilePage';
import TimeEntryPage from './pages/time-entry/TimeEntryPage';
import ProductionTasksPage from './pages/production/ProductionTasksPage';
import ServiceJobsPage from './pages/service/ServiceJobsPage';
import SyncPage from './pages/sync/SyncPage';

import SettingsPage from './pages/settings/SettingsPage';

// Admin & Team Management Pages
import UserManagementPage from './pages/UserManagementPage';
import TeamManagementPage from './pages/TeamManagementPage';
import TabPermissionsPage from './pages/admin/TabPermissionsPage';
import AiInsightEmailsPage from './pages/admin/AiInsightEmailsPage';
import OrgHierarchyPage from './pages/OrgHierarchyPage';
import TeamOverviewPage from './pages/team/TeamOverviewPage';
import MyAssignedTasksPage from './pages/tasks/MyAssignedTasksPage';
import EmployeesPage from './pages/EmployeesPage';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import ReportsPage from './pages/reports/ReportsPage';
import KpiDashboardPage from './pages/kpi/KpiDashboardPage';
import KpiAiDashboardPage from './pages/kpi/KpiAiDashboardPage';
import HelpSupportPage from './pages/support/HelpSupportPage';

/* ─── Dark-mode-aware Toaster ─── */
const StyledToaster = () => {
  const { darkMode } = useTheme();

  return (
    <Toaster
      position="top-center"
      gutter={16}
      toastOptions={{
        duration: 4000,
        style: {
          minWidth: '380px',
          maxWidth: '480px',
          background: darkMode ? '#1F2937' : '#FFFFFF',
          color: darkMode ? '#F3F4F6' : '#1F2937',
          borderRadius: '16px',
          padding: '18px 22px',
          boxShadow: darkMode
            ? '0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06)'
            : '0 20px 40px rgba(0,0,0,0.08), 0 10px 20px rgba(0,0,0,0.04)',
          border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #E5E7EB',
          fontSize: '15px',
          fontWeight: '500',
          lineHeight: '1.6',
        },
        success: {
          duration: 4000,
          icon: '✅',
          style: {
            background: darkMode ? '#1A2E1A' : '#F0FDF4',
            border: darkMode ? '1.5px solid rgba(34,197,94,0.3)' : '1.5px solid #BBF7D0',
            color: darkMode ? '#86EFAC' : '#065F46',
          },
          iconTheme: {
            primary: darkMode ? '#86EFAC' : '#065F46',
            secondary: darkMode ? '#1A2E1A' : '#F0FDF4',
          },
        },
        error: {
          duration: 5000,
          icon: '❌',
          style: {
            background: darkMode ? '#2E1A1A' : '#FEF2F2',
            border: darkMode ? '1.5px solid rgba(239,68,68,0.3)' : '1.5px solid #FECACA',
            color: darkMode ? '#FCA5A5' : '#991B1B',
          },
          iconTheme: {
            primary: darkMode ? '#FCA5A5' : '#991B1B',
            secondary: darkMode ? '#2E1A1A' : '#FEF2F2',
          },
        },
        loading: {
          duration: Infinity,
          style: {
            background: darkMode ? '#1E1A2E' : '#F5F3FF',
            border: darkMode ? '1.5px solid rgba(139,92,246,0.3)' : '1.5px solid #DDD6FE',
            color: darkMode ? '#C4B5FD' : '#5B21B6',
          },
          iconTheme: {
            primary: darkMode ? '#C4B5FD' : '#5B21B6',
            secondary: darkMode ? '#1E1A2E' : '#F5F3FF',
          },
        },
      }}
    />
  );
};

// Component to conditionally show bug button
const ConditionalBugButton = () => {
  const { user, isLoading } = useAuth();
  
  // Only show button if user is logged in and not loading
  if (isLoading || !user) {
    return null;
  }
  
  return <GlobalBugButton />;
};

function App() {
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);

  // Set initialized to true immediately since MSAL is completely removed
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <OperisLoader size="lg" message={t('app.initializing')} />
      </div>
    );
  }

  return (
    <>
      <StyledToaster />
        <TabPermissionProvider>
        <SignalRProvider>
        <TimerProvider>
        <Router>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />

              <Route element={<MainLayout />}>
                <Route path="/dashboard" element={<TabGuard tabKey="dashboard"><Dashboard /></TabGuard>} />
                <Route path="/time-entries" element={<TabGuard tabKey="time-entries"><TimeEntryPage /></TabGuard>} />
                <Route path="/time-entries/new" element={<TabGuard tabKey="time-entries"><TimeEntryPage /></TabGuard>} />
                <Route path="/production-tasks" element={<TabGuard tabKey="production-tasks"><ProductionTasksPage /></TabGuard>} />
                <Route path="/service-jobs" element={<ServiceJobsPage />} />
                <Route path="/sync" element={<TabGuard tabKey="sync"><SyncPage /></TabGuard>} />
                <Route path="/employees" element={<TabGuard tabKey="employees"><EmployeesPage /></TabGuard>} />

                {/* User Profile — not guarded (always accessible) */}
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/settings" element={<SettingsPage />} />

                {/* Admin Routes */}
                <Route path="/admin/userManagement" element={<TabGuard tabKey="admin-user-management"><UserManagementPage /></TabGuard>} />
                <Route path="/admin/teams" element={<TabGuard tabKey="admin-teams"><TeamManagementPage /></TabGuard>} />
                <Route path="/admin/tab-permissions" element={<TabPermissionsPage />} />
                <Route path="/admin/ai-insight-emails" element={<AiInsightEmailsPage />} />
                <Route path="/team-overview" element={<TabGuard tabKey="team-overview"><TeamOverviewPage /></TabGuard>} />
                <Route path="/org-hierarchy" element={<TabGuard tabKey="org-hierarchy"><OrgHierarchyPage /></TabGuard>} />
                <Route path="/my-tasks" element={<TabGuard tabKey="my-tasks"><MyAssignedTasksPage /></TabGuard>} />
                <Route path="/reports" element={<TabGuard tabKey="reports"><ReportsPage /></TabGuard>} />
                <Route path="/kpi-dashboard" element={<TabGuard tabKey="kpi-dashboard"><KpiDashboardPage /></TabGuard>} />
                <Route path="/kpi-ai-analytics" element={<TabGuard tabKey="kpi-dashboard"><KpiAiDashboardPage /></TabGuard>} />
                <Route path="/help" element={<HelpSupportPage />} />
              </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          {/* Add Conditional Bug Button - only shows when user is logged in */}
          <ConditionalBugButton />
          
        </Router>
        </TimerProvider>
        </SignalRProvider>
        </TabPermissionProvider>
    </>
  );
}

export default App;
